import React, {useState, useEffect, useRef} from "react";
import {Container} from "@adobe/aem-react-editable-components";
import Slider from "react-slick";
import {ErrorBoundary} from "react-error-boundary";
import {urlHasParam} from "../../utils/js/params";
import {
	Button,
	Column,
	Headline,
	Logo,
	Section,
	Subtitle,
	Title,
	HeadingTitle,
	CaseStudyContainer,
	CaseStudySliderContainer,
	FullWidthSliderContainer,
	CaseStudyCardWrapper,
	SliderProgress
} from "./CaseStudy.style";
import {InfiosContainer} from "../../utils/styled/_grid.style";
import Icon from "../Icon/Icon";
import {sizes} from "../../utils/styled/_variables.styled";
import {responsive} from "../RelatedContentSection/Slider";
import RelatedSliderProgress from "../RelatedContentSection/Slider/Progress";


const CaseStudyCard = ({column, filter}) => (
	<CaseStudyCardWrapper>
		<div className="case-study-card">
			<div className="case-study-tag">
				{column.subHeading}
			</div>

			<div className="case-study-image">
				<Logo withFilter={filter} minHeight={column.image.props?.originalHeight}>
					{column.image}
				</Logo>
			</div>

			<div className="case-study-hover-content">
				<div className="case-study-title">
					{column.title}
				</div>

				<div className="case-study-description">
					{column.subtitle}
				</div>
			</div>

			<div className="case-study-button">
				{column.button}
			</div>
		</div>
	</CaseStudyCardWrapper>
);

const CaseStudySlider = ({columns, id, filter, title, subtitle}) => {
	const [currentSlide, setCurrentSlide] = useState(0);
	const [isFirstSlide, setIsFirstSlide] = useState(true);
	const [isLastSlide, setIsLastSlide] = useState(false);
	const [slidesToShow, setSlidesToShow] = useState(3);
	const sliderRef = useRef(null);
	const [totalScrolls, setTotalScrolls] = useState(0)

	const getSlidesToShowForWidth = (width) => {
		if (width < sizes.mobile) {
			return 1.2;
		} else if (width < sizes.tabletLarge) {
			return 2;
		} else if (width < sizes.desktop) {
			return 3;
		}
		return 3;
	};

	useEffect(() => {
		const updateSlidesToShow = () => {
			const newSlidesToShow = getSlidesToShowForWidth(window.innerWidth);
			setSlidesToShow(newSlidesToShow);

			const maxSlideIndex = Math.max(0, columns.length - newSlidesToShow);
			setIsLastSlide(currentSlide >= maxSlideIndex);
		};

		updateSlidesToShow();
		window.addEventListener('resize', updateSlidesToShow);

		return () => window.removeEventListener('resize', updateSlidesToShow);
	}, [columns.length, currentSlide]);

	useEffect(() => {
		const initialSlidesToShow = getSlidesToShowForWidth(window.innerWidth);
		const maxSlideIndex = Math.max(0, columns.length - initialSlidesToShow);
		setIsLastSlide(currentSlide >= maxSlideIndex && columns.length > initialSlidesToShow);
		setIsFirstSlide(currentSlide === 0);
	}, [columns.length]);


	const carouselSettings = {
		dots: false,
		arrows: false,
		swipe: true,
		infinite: false,
		slidesToShow: 3,
		slidesToScroll: 1,
		mobileFirst: true,
		initialSlide: 0,
		centerMode: false,
		variableWidth: false,
		rows: 1,
		onReInit: () => {
			const currentBreakpoint = window.innerWidth;

			const breakpointSetting = [...responsive]
				.reverse()
				.find(item => currentBreakpoint <= item.breakpoint)?.settings;

			if (breakpointSetting) {
				const numberOfScrolls = Math.ceil((columns?.length) / breakpointSetting.slidesToScroll);
				if (numberOfScrolls !== totalScrolls) {
					setTotalScrolls(numberOfScrolls);
					if(numberOfScrolls === 1) setIsLastSlide(true)
				}
			} else {
				console.error('No matching breakpoint found');
			}
		},
		beforeChange: (prevIndex, nextIndex) => {
			setCurrentSlide(Math.max(0, Math.min(totalScrolls, currentSlide + (nextIndex > prevIndex ? 1 : -1))));
			setIsFirstSlide(nextIndex === 0);
			setIsLastSlide(!!(nextIndex % 1) || nextIndex === columns?.length - 1)
		},
		afterChange: (index) => {
			setIsFirstSlide(index === 0);
			setIsLastSlide(!!(index % 1) || index === columns?.length - 1)
		},
		responsive: responsive
	};

	return (
		<CaseStudySliderContainer
			id={id}
			className="case-study-slider"
			isLastSlide={isLastSlide}
			numberSlides={columns.length}
			currentSlide={currentSlide}
			totalScrolls={totalScrolls}
		>
			<div className="rel-content__section__header">
				<div className="rel-content__title-container">
					{subtitle && (
						<div className="rel-content__subtitle">
							{subtitle}
						</div>
					)}
					<div className="rel-content__title">
						<div className="rel-content__title__text">
							{title}
						</div>
					</div>
				</div>
				<div className="rel-content__section__navigation">
                    <span
											className={`rel-content__section__navigation__button ${isFirstSlide ? "disabled" : ""}`}
											onClick={() => !isFirstSlide && sliderRef.current?.slickPrev()}
										>
                        <Icon icon="triangle-arrow-right"/>
                    </span>
					<span
						className={`rel-content__section__navigation__button ${isLastSlide || columns.length <= slidesToShow ? "disabled" : ""}`}
						onClick={() => !(isLastSlide || columns.length <= slidesToShow) && sliderRef.current?.slickNext()}
					>
                        <Icon icon="triangle-arrow-right" color="yellow"/>
                    </span>
				</div>
			</div>
			<RelatedSliderProgress />
			<FullWidthSliderContainer>
				<div className="rel-content__section__items">
					<Slider ref={sliderRef} {...carouselSettings}>
						{columns.map((column, index) => (
							<CaseStudyCard
								key={`case-study-card-${index}`}
								column={column}
								filter={filter}
							/>
						))}
					</Slider>
				</div>
			</FullWidthSliderContainer>
		</CaseStudySliderContainer>
	);
};

export class CaseStudy extends Container {
	constructor(props) {
		super(props);
		this.sectionRef = React.createRef();
		this.title = this.childComponents[1];
		this.columns = this.generateDynamicColumns();
	}

	componentDidMount() {
		this.sectionRef?.current.parentElement.classList.add(`unset__grid`);
	}

	generateDynamicColumns() {
		const columns = [];
		const elementsPerColumn = 5;
		const childStartIndex = 2;

		const maxColumns = Math.floor((this.childComponents.length - childStartIndex) / elementsPerColumn);

		for (let i = 0; i < maxColumns; i++) {
			const baseIndex = childStartIndex + (i * elementsPerColumn);

			if (baseIndex + 4 < this.childComponents.length) {
				columns.push({
					subHeading: this.childComponents[baseIndex],
					image: this.childComponents[baseIndex + 1],
					title: this.childComponents[baseIndex + 2],
					subtitle: this.childComponents[baseIndex + 3],
					button: this.childComponents[baseIndex + 4]
				});
			}
		}

		return columns;
	}

	render() {
		const shouldRenderPreview = this.props.isInEditor || this.columns.length <= 2;

		return (
			<Section id={this.props.id} ref={this.sectionRef} isInEditor={this.props.isInEditor}>
				{shouldRenderPreview ? (
					<InfiosContainer>
						<Headline>{this.title}</Headline>
						<div className="case-study-editor-preview">
							{this.columns.map((column, index) => (
								<Column>
									<HeadingTitle>{column.subHeading}</HeadingTitle>
									<Logo withFilter={this.props.filter}
												minHeight={column.image.props?.originalHeight}>{column.image}</Logo>
									<Title>{column.title}</Title>
									<Subtitle>{column.subtitle}</Subtitle>
									<Button>{column.button}</Button>
								</Column>
							))}
						</div>
					</InfiosContainer>
				) : (
					<InfiosContainer>
						<CaseStudySlider
							columns={this.columns}
							id={this.props.id}
							filter={this.props.filter}
							title={this.title}
						/>
					</InfiosContainer>
				)}
			</Section>
		)
	}
}

const CaseStudyErrors = props => {
	return (
		<ErrorBoundary fallback={props.isInEditor || urlHasParam('wcmmode=disabled') ?
			<div style={{minHeight: '50px', display: 'flex', alignItems: 'center', backgroundColor: '#ffe1db'}}><p>This
				component is currently experiencing errors. Please try again later.</p></div> : <div></div>}>
			<CaseStudy {...props} />
		</ErrorBoundary>
	)
}

export default CaseStudyErrors;
